import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  Loader2,
  MessageCircle,
  Send,
  Shield,
} from "lucide-react";
import {
  api,
  type MessagingChannel,
  type MessagingPreviewResponse,
  type MessagingSendResponse,
  type MessagingSpec,
} from "@/lib/api";
import {
  AudienceSelector,
  defaultAudience,
  type AudienceSelection,
} from "@/components/AudienceSelector";
import { MessagingTargetPicker } from "@/components/MessagingTargetPicker";
import { MessagingJobMonitor } from "@/components/MessagingJobMonitor";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { cn } from "@/lib/utils";

const CHANNELS: Array<{
  id: MessagingChannel;
  label: string;
  icon: string;
  desc: string;
}> = [
  { id: "playtomic_dm", label: "Playtomic DM", icon: "🎾", desc: "1-a-1 in-app · audience filtrata" },
  { id: "whatsapp_group", label: "WhatsApp Gruppi", icon: "💬", desc: "community + bacheca" },
  { id: "playtomic_broadcast", label: "Broadcast Playtomic", icon: "📣", desc: "chat community / torneo" },
  { id: "email", label: "Email Marketing", icon: "📧", desc: "audience filtrata · SMTP Aruba" },
];

function fmtDuration(sec: number): string {
  if (!sec || sec <= 0) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h > 0) return `~${h}h ${m}m`;
  return `~${m}m`;
}

export default function MessagingHub() {
  const qc = useQueryClient();
  const [channel, setChannel] = useState<MessagingChannel>("playtomic_dm");
  const [audience, setAudience] = useState<AudienceSelection>(defaultAudience());
  const [targets, setTargets] = useState<string[]>([]);
  const [text, setText] = useState(
    "Ciao {nome}, sono Davide di Moving Padel Monza. {link}",
  );
  const [subject, setSubject] = useState("");
  const [link, setLink] = useState("");
  const [label, setLabel] = useState("");
  const [jitterMin, setJitterMin] = useState(180);
  const [jitterMax, setJitterMax] = useState(600);
  const [previewData, setPreviewData] = useState<MessagingPreviewResponse | null>(null);
  const [sendMsg, setSendMsg] = useState<string | null>(null);

  const health = useQuery({
    queryKey: ["messaging-health"],
    queryFn: () => api.messagingHealth(),
    staleTime: 60_000,
  });

  const isAudienceChannel = channel === "playtomic_dm" || channel === "email";
  const isEmail = channel === "email";

  const buildSpec = (overrides: Partial<MessagingSpec> = {}): MessagingSpec => ({
    channel,
    label: label.trim(),
    text,
    subject: subject.trim(),
    link: link.trim(),
    audience: isAudienceChannel
      ? {
          audience: audience.audience,
          min_tournaments: audience.min_tournaments,
          since: audience.since,
          level_ranges: audience.level_ranges,
          tournament_name_contains: audience.tournament_name_contains,
          gender: audience.gender,
        }
      : undefined,
    targets: isAudienceChannel ? undefined : targets,
    jitter_min_sec: jitterMin,
    jitter_max_sec: jitterMax,
    ...overrides,
  });

  const previewMut = useMutation({
    mutationFn: () => api.messagingPreview(buildSpec()),
    onSuccess: (d) => {
      setPreviewData(d);
      setSendMsg(null);
    },
  });

  const sendMut = useMutation({
    mutationFn: () => api.messagingSend(buildSpec({ dry_run: false, confirm: true })),
    onSuccess: (d: MessagingSendResponse) => {
      setSendMsg(
        d.job_id
          ? `✅ Invio avviato — job ${d.job_id} (${d.launcher}). Segui il progresso qui sotto.`
          : "Invio avviato.",
      );
      qc.invalidateQueries({ queryKey: ["messaging-jobs"] });
    },
    onError: (e: unknown) => {
      const detail = (e as { body?: { detail?: string } })?.body?.detail;
      setSendMsg(`❌ Errore: ${detail ?? String(e)}`);
    },
  });

  const canConfigure = useMemo(() => {
    if (!text.trim()) return false;
    if (isEmail && !subject.trim()) return false;
    if (isAudienceChannel) return true;
    return targets.length > 0;
  }, [text, isEmail, subject, isAudienceChannel, targets]);

  const stats = previewData?.stats;
  const net = stats?.net_recipients ?? 0;

  const doLiveSend = () => {
    if (!previewData) return;
    const ok = window.confirm(
      `Confermi l'invio REALE a ${net} destinatari sul canale "${channel}"?\n` +
        `Label: ${label || "(nessuna)"}\nNON annullabile una volta partito.`,
    );
    if (ok) sendMut.mutate();
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <MessageCircle className="h-6 w-6 text-moving-600" /> Messaging Hub
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Componi e invia su Playtomic DM, WhatsApp gruppi e broadcast — con
            audience condiviso, opt-out unificato e quiet-hours 09–21.
          </p>
        </div>
        {health.data ? (
          <div className="hidden shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 sm:block">
            <div className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-moving-600" />
              <span className="font-semibold">Opt-out unificato</span>
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              {health.data.optout.emails} email · {health.data.optout.uids} uid ·{" "}
              {health.data.optout.phones} tel
            </div>
            <div className="mt-0.5 text-[11px]">
              Playtomic:{" "}
              {health.data.playtomic.ok ? (
                <span className="text-emerald-600">● connesso</span>
              ) : (
                <span className="text-red-600">● offline</span>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Colonna composer */}
        <div className="space-y-5 lg:col-span-2">
          {/* 1 · Canale */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-moving-500 text-xs font-bold text-white">
                1
              </span>
              <h2 className="font-semibold">Canale</h2>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {CHANNELS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setChannel(c.id);
                    setPreviewData(null);
                  }}
                  className={cn(
                    "rounded-lg border-2 p-3 text-left transition",
                    channel === c.id
                      ? "border-moving-500 bg-moving-50"
                      : "border-slate-200 hover:border-slate-300",
                  )}
                >
                  <div className="text-lg">{c.icon}</div>
                  <div className="mt-1 text-sm font-semibold">{c.label}</div>
                  <div className="text-xs text-slate-500">{c.desc}</div>
                </button>
              ))}
            </div>
          </section>

          {/* 2 · Destinatari */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-moving-500 text-xs font-bold text-white">
                2
              </span>
              <h2 className="font-semibold">Destinatari</h2>
            </div>
            {isAudienceChannel ? (
              <AudienceSelector
                channel={isEmail ? "email" : "whatsapp"}
                value={audience}
                onChange={(v) => {
                  setAudience(v);
                  setPreviewData(null);
                }}
                hidePreview
              />
            ) : (
              <MessagingTargetPicker
                channel={channel}
                selected={targets}
                onChange={(t) => {
                  setTargets(t);
                  setPreviewData(null);
                }}
              />
            )}
          </section>

          {/* 3 · Messaggio */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-moving-500 text-xs font-bold text-white">
                3
              </span>
              <h2 className="font-semibold">Messaggio</h2>
              <span className="ml-auto text-xs text-slate-400">
                variabili: {"{nome}"} {"{link}"}
              </span>
            </div>
            {isEmail ? (
              <div className="mb-3">
                <Label className="text-xs">Oggetto email</Label>
                <Input
                  value={subject}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    setPreviewData(null);
                  }}
                  placeholder="es. Torneo di sabato — iscrizioni aperte"
                />
              </div>
            ) : null}
            <Textarea
              rows={5}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setPreviewData(null);
              }}
              placeholder="Ciao {nome}, ..."
            />
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Link (sostituisce {"{link}"})</Label>
                <Input
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://app.playtomic.io/tournaments/..."
                />
              </div>
              <div>
                <Label className="text-xs">Label campagna (per audit + dedup)</Label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="es. giro-8-alle-18"
                />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <span>Jitter invii (sec):</span>
              <Input
                type="number"
                value={jitterMin}
                onChange={(e) => setJitterMin(parseInt(e.target.value) || 10)}
                className="w-20"
              />
              <span>→</span>
              <Input
                type="number"
                value={jitterMax}
                onChange={(e) => setJitterMax(parseInt(e.target.value) || 10)}
                className="w-20"
              />
              <span className="text-slate-400">· {text.length} caratteri</span>
            </div>
          </section>
        </div>

        {/* Colonna preview + invio */}
        <div className="space-y-5">
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 font-semibold">Anteprima invio</h2>
            <Button
              onClick={() => previewMut.mutate()}
              disabled={!canConfigure || previewMut.isPending}
              className="w-full"
            >
              {previewMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              Calcola destinatari (dry-run)
            </Button>

            {previewMut.isError ? (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {String(
                    (previewMut.error as { body?: { detail?: string } })?.body
                      ?.detail ?? previewMut.error,
                  )}
                </span>
              </div>
            ) : null}

            {stats ? (
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Audience grezza</span>
                  <span className="font-semibold">{stats.raw}</span>
                </div>
                {stats.blocked_optout > 0 ? (
                  <div className="flex justify-between text-slate-500">
                    <span>− opt-out (unificato)</span>
                    <span>−{stats.blocked_optout}</span>
                  </div>
                ) : null}
                {stats.already_sent > 0 ? (
                  <div className="flex justify-between text-slate-500">
                    <span>− già inviati (label)</span>
                    <span>−{stats.already_sent}</span>
                  </div>
                ) : null}
                <div className="my-1 border-t border-slate-100" />
                <div className="flex items-center justify-between rounded-lg bg-moving-50 px-3 py-2">
                  <span className="font-semibold text-moving-800">
                    Destinatari netti
                  </span>
                  <span className="text-xl font-bold text-moving-700">{net}</span>
                </div>
                <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                  ⏱ Stima durata: {fmtDuration(previewData?.estimated_duration_sec ?? 0)}
                </div>

                {previewData?.sample?.length ? (
                  <div className="mt-2">
                    <div className="mb-1 text-[11px] font-semibold uppercase text-slate-500">
                      Anteprima messaggi
                    </div>
                    <div className="space-y-1.5">
                      {previewData.sample.slice(0, 3).map((s, i) => (
                        <div
                          key={i}
                          className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-700"
                        >
                          <span className="font-semibold">{s.name || s.target}:</span>{" "}
                          {s.message}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          {/* Invio reale */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-2 font-semibold">Invio reale</h2>
            <p className="mb-3 text-xs text-slate-500">
              Disponibile dopo un dry-run riuscito. Parte un job separato con
              jitter e quiet-hours; puoi seguirlo nel monitor in basso.
            </p>
            <Button
              variant="primary"
              onClick={doLiveSend}
              disabled={!previewData || net <= 0 || sendMut.isPending}
              className="w-full"
            >
              {sendMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Invia davvero ({net})
            </Button>
            {sendMsg ? (
              <div
                className={cn(
                  "mt-3 flex items-start gap-2 rounded-md p-2.5 text-xs",
                  sendMsg.startsWith("❌")
                    ? "bg-red-50 text-red-800"
                    : "bg-emerald-50 text-emerald-800",
                )}
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{sendMsg}</span>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      {/* Job monitor */}
      <div className="mt-6">
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Monitor invii</h2>
        <MessagingJobMonitor />
      </div>
    </div>
  );
}
